// journal-delta-known-secret-text-fields.ts — phase 04a B11 (spec V2 §4.2).
//
// `journal.jsonl` là append-only: che sai thì KHÔNG sửa được. Nên che theo
// ALLOWLIST TƯỜNG MINH từng kind — không regex đoán field — và chỉ field text
// tự do (lý do/evidence, tail result, khoảng trống worker khai, quyết định
// worker, text câu hỏi/nhãn/câu trả lời). KHÔNG BAO GIỜ field cấu trúc:
// `event_id`, `operation_id`, `operation_intent.params`, `remote_id`, sha,
// path, `question_id`, `request_id`, `kind`, `ticket`/`ticket_id`, `actor`,
// `directive_id`, `option_id`, id của option — reducer/reconciler/dedupe đọc
// chúng, đổi là đổi nghĩa journal. Ngoại lệ (review 04a L2): MỌI chuỗi trong
// `worker_decisions` (kể cả key `id`) là text do worker viết, không ai replay
// theo chúng ⇒ che hết.
//
// MỌI kind của mission-delta-kinds.ts phải nằm ở ĐÚNG MỘT trong hai bảng dưới
// (test mission-store-commit-redaction-covers-every-delta-kind.test.ts đọc
// file kind và đỏ khi thêm kind chưa phân loại; nó cũng phủ từng field).
import type { MissionDelta, MissionDeltaKind } from "./mission-state-types.js";
import { redactKnownValues } from "./secret-redactor.js";

/** `text`: chuỗi hoặc mảng chuỗi. `deep`: mọi chuỗi lồng (giữ key). `option_labels`: chỉ `label` trong mảng option. */
type TextFieldShape = "text" | "deep" | "option_labels";

export const JOURNAL_DELTA_TEXT_FIELDS_BY_KIND: Partial<
  Record<MissionDeltaKind, Readonly<Record<string, TextFieldShape>>>
> = {
  attempt_finished: {
    reasons: "text",
    schema_error_detail: "text",
    raw_result_tail: "text",
    uncovered_areas: "text",
    remaining_work: "text",
    worker_decisions: "deep",
  },
  attempt_recovered: { reason: "text", evidence: "text" },
  operation_result: { reason: "text" },
  directive_rejected: { reason: "text" },
  g3_withdrawn_for_candidate_change: { reason: "text" },
  decision_question_asked: { summary: "text", options: "option_labels" },
  decision_auto_applied: { option_label: "text" },
  decision_auto_skipped: { reason: "text" },
  decision_answered: { value: "text" },
};

/** Kind chỉ mang enum/id/sha/path/số — đo producer 06/10 (dump 04a), nguyên văn. */
export const JOURNAL_DELTA_KINDS_WITHOUT_FREE_TEXT: readonly MissionDeltaKind[] = [
  "mission_started",
  "mission_status_changed",
  "mission_workspace_ready",
  "ticket_state_changed",
  "attempt_started",
  "operation_intent",
  "review_receipt_written",
  "journal_recovered",
  "gate_approved",
  "abort_challenge_issued",
  "worktree_reset_on_reclaim",
  "mission_pause_applied",
  "mission_resume_applied",
  "candidate_review_finished",
  "decision_question_retired",
];

function redactText(value: unknown, values: readonly string[]): unknown {
  if (typeof value === "string") return redactKnownValues(value, values);
  if (Array.isArray(value)) return value.map((v) => redactText(v, values));
  return value;
}

function redactDeep(value: unknown, values: readonly string[]): unknown {
  if (typeof value === "string") return redactKnownValues(value, values);
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, values));
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, redactDeep(v, values)]),
  );
}

function redactOptionLabels(value: unknown, values: readonly string[]): unknown {
  if (!Array.isArray(value)) return value;
  return value.map((option) => {
    if (option === null || typeof option !== "object" || Array.isArray(option)) return option;
    const record = option as Record<string, unknown>;
    return "label" in record ? { ...record, label: redactText(record.label, values) } : record;
  });
}

const SHAPE_REDACTORS: Record<TextFieldShape, (v: unknown, values: readonly string[]) => unknown> =
  {
    text: redactText,
    deep: redactDeep,
    option_labels: redactOptionLabels,
  };

/**
 * Bản sao delta với field text của allowlist đã che theo known-values. Không
 * mutate `delta`. `values` rỗng hoặc kind không có field text ⇒ trả CHÍNH
 * `delta` (byte-identical v1 — golden I1 (c)).
 */
export function redactJournalDeltaTextFields(
  delta: MissionDelta,
  values: readonly string[],
): MissionDelta {
  const fields = JOURNAL_DELTA_TEXT_FIELDS_BY_KIND[delta.kind];
  if (values.length === 0 || fields === undefined) return delta;
  const next: MissionDelta = { ...delta };
  for (const [field, shape] of Object.entries(fields)) {
    if (field in next) next[field] = SHAPE_REDACTORS[shape](next[field], values);
  }
  return next;
}
